import React from 'react';
import { FormSubmitClickEvent } from '@progress/kendo-react-form';
import { StepperChangeEvent } from '@progress/kendo-react-layout';
import { models } from '../../../utils/cms-client';
import { PersonDataContext } from '../../../contexts/person-data-context';
import { KenticoDataContext } from '../../../contexts/kentico-data-context';
import { PreferencesContext } from '../../../contexts/preferences-context';
import { FormContext, IFormContext } from '../../../contexts/form-context';
import { updatedClaimList } from './generators';
import {
  CustomStepProps,
  formatPhoneNumber,
  handleSmsNotification,
  moveToStep,
  processNavigationObject,
  transformFormStepsForStepper,
} from './utils';
import {
  handleUseEffectContexts,
  handleUseEffectInit,
  handleUseEffectStorybook,
  onStepSubmit,
} from './handlers';
import useHandlebars from '../../../hooks/use-handlebars';
import {
  getCorpLeaveIncrements,
  postPreferences,
} from '../../../utils/web-apis-client';
import {
  useAutosaveGet,
  useAutosavePost,
  useVBCoverage,
  usePtoChoice,
  usePreferences,
  useLlPlanPath,
  queryKeys,
} from '../../../hooks/use-data';
import { queryClient } from '../../../query-client';
import { IAutoSaveResponse } from '../../../data-models';
import {
  getPreferenceMethods,
  setFieldsFromRemoteConfig,
  setLeaveOnlyFlag,
  getVBCoverage,
  showSteps,
} from './context-helpers';
import { ComponentProps } from '.';
import { scrollToElementByName } from '../../../utils';

const useFormWizard = (p: ComponentProps) => {
  const { data: communicationPreferences } = usePreferences();
  // path processing
  const {
    lastPath,
    task,
    defaults: stateDefaults,
    leaveLogicPlanId,
  } = processNavigationObject(p);

  const [planIdFromQuery] = React.useState(() =>
    new URLSearchParams(p.location?.search ?? '').get('id'),
  );
  const { data: llPlanPathData, isFetching: llPlanPathFetching } =
    useLlPlanPath(planIdFromQuery) ?? {};
  const queryPreFill =
    typeof llPlanPathData === 'object' && llPlanPathData !== null
      ? llPlanPathData?.preFill
      : undefined;
  const prefillLoading = !!planIdFromQuery && llPlanPathFetching;

  const defaults = { ...queryPreFill, ...stateDefaults };
  const llPrefillData = defaults;
  const hasLlPrefillData =
    llPrefillData !== null &&
    typeof llPrefillData === 'object' &&
    Object.keys(llPrefillData).length > 0;
  const hasStateDefaults =
    !!stateDefaults &&
    typeof stateDefaults === 'object' &&
    Object.keys(stateDefaults).length > 0;

  // Kentico Form* model states
  const [form, setForm] = React.useState<models.Form | undefined>(p.form);
  const [formSteps, setFormSteps] = React.useState<models.FormStep[]>(
    p.form?.elements.steps?.linkedItems || [],
  );
  const [formStepIndex, setFormStepIndex] = React.useState(0);

  // form context from claims/leaves/etc
  const { formContext, setFormContext, allowNavBack } =
    React.useContext(FormContext);
  const { personData, refreshClaimsInProcess } =
    React.useContext(PersonDataContext);
  const { employerPreferences } = React.useContext(PreferencesContext);
  // Stepper states
  const [formState, setFormState] = React.useState(defaults);
  const [prefillApplied, setPrefillApplied] = React.useState(
    () => !planIdFromQuery && !hasStateDefaults,
  );
  const [currentStepIndex, setCurrentStepIndex] = React.useState(
    p.stepIndex ?? 0,
  );
  const [notFound, setNotFound] = React.useState(false);
  const [steps, setSteps] = React.useState<CustomStepProps[]>([]);
  const [submitted, setSubmitted] = React.useState(p.submitted || false);
  const labelMarkdown = useHandlebars(form?.elements.label?.value, formContext);

  // misc states
  const [showError, setShowError] = React.useState(p.showError || false);
  const [submitting, setSubmitting] = React.useState(p.submitting || false);
  const [displayConfirmationModal, setDisplayConfirmationModal] =
    React.useState(false);
  const [displayRestartWarningModal, setDisplayRestartWarningModal] =
    React.useState(false);
  const [scrollToElement, setScrollToElement] = React.useState('');
  const previousStepIndex = React.useRef(currentStepIndex);

  React.useEffect(() => {
    const stepChanged = previousStepIndex.current !== currentStepIndex;
    previousStepIndex.current = currentStepIndex;

    if (stepChanged && scrollToElement) {
      scrollToElementByName(scrollToElement);
      setScrollToElement('');
    }
  }, [currentStepIndex, scrollToElement]);

  //state value for telling the modal which step to return to when stepper is clicked
  const [stepGoBack, setStepGoBack] = React.useState(-1);
  const [validationErrorCount, setValidationErrorCount] = React.useState(0);
  const { config } = React.useContext(KenticoDataContext);
  const { data: rawAutosaveData, isLoading: autosaveIsFetching } =
    useAutosaveGet(lastPath);
  const { mutate: mutateAutosavePost } = useAutosavePost(lastPath);
  const { data: vbCoverageData } = useVBCoverage();
  const { data: ptoChoiceData } = usePtoChoice();

  const autosaveData: IAutoSaveResponse | undefined = hasLlPrefillData
    ? {
        ...rawAutosaveData,
        exists: true,
        result: {
          formName: lastPath,
          formStepIndex: 0,
          formStepName: '',
          savedTimestamp: new Date().toISOString(),
          submitted: false,
          ...rawAutosaveData?.result,
          data: llPrefillData,
        },
      }
    : rawAutosaveData;

  const getPTOChoice = async (setFormContext: Function) => {
    setFormContext((fc: IFormContext) => ({
      ...fc,
      ptoChoice: ptoChoiceData,
    }));
  };

  const communicationPreferencesData = async (setFormContext: Function) => {
    const hasNotificationsStored =
      !!communicationPreferences?.communicationPreferences?.some(
        (c: any) => c.preferenceValue === 'Texting',
      );
    const rawNumber = communicationPreferences?.communicationPreferences?.find(
      (c: any) => c.preferenceType === 'Texting',
    )?.phoneNumber?.telephoneNo;
    setFormContext((fc: IFormContext) => ({
      ...fc,
      currentSmsNumber: rawNumber,
      currentSmsNumberFormatted: formatPhoneNumber(rawNumber),
    }));
    if (hasNotificationsStored === false) {
      setFormContext((fc: IFormContext) => ({
        ...fc,
        smsOptIn: 'Opt-Out',
      }));
    }
  };

  React.useEffect(() => {
    if (communicationPreferences && !formContext?.smsOptIn) {
      const smsPref = communicationPreferences?.communicationPreferences?.find(
        (c) => c.preferenceType === 'Texting',
      );
      setFormContext((fc: IFormContext) => ({
        ...fc,
        smsOptIn: smsPref?.preferenceValue,
      }));
    }
  }, [communicationPreferences, formContext?.smsOptIn, setFormContext]);

  // this is only here for storybook
  React.useEffect(() => {
    handleUseEffectStorybook(
      p,
      form,
      setForm,
      setFormSteps,
      setSubmitted,
      setShowError,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.form, p.submitted, p.showError]);

  React.useEffect(() => {
    setSteps(transformFormStepsForStepper(formSteps));
  }, [formSteps]);

  React.useEffect(() => {
    personData &&
      setFormContext((fc: any) => {
        if (leaveLogicPlanId) {
          fc.leaveLogicPlanId = leaveLogicPlanId;
        }
        const { claimList, ipd } = updatedClaimList(personData);
        return { ...fc, claimList, ...ipd };
      });
  }, [personData, setFormContext, leaveLogicPlanId]);

  React.useEffect(() => {
    handleUseEffectContexts(
      personData,
      employerPreferences,
      setFormContext,
      task,
      defaults,
      config,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFormContext]);

  React.useEffect(() => {
    if (prefillApplied || prefillLoading) {
      return;
    }
    if (hasLlPrefillData) {
      queryClient.setQueryData([queryKeys.autosaveGet, lastPath], autosaveData);
      setFormState((prev: any) => ({ ...prev, ...llPrefillData }));
      setFormContext((fc: IFormContext) => ({
        ...fc,
        defaults: { ...fc.defaults, ...llPrefillData },
      }));
    }
    setPrefillApplied(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillApplied, prefillLoading, hasLlPrefillData]);

  React.useEffect(() => {
    setLeaveOnlyFlag(formContext, setFormContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formContext.formName]);

  React.useEffect(() => {
    handleUseEffectInit({
      p,
      personData,
      employerPreferences,
      setFormContext,
      lastPath,
      setNotFound,
      setForm,
      setFormSteps,
      task,
      defaults,
      setSubmitted,
      setCurrentStepIndex,
      setFormStepIndex,
    });
    getPreferenceMethods(setFormContext, communicationPreferences);
    setFieldsFromRemoteConfig(setFormContext, personData?.person?.companyCode);
    if (vbCoverageData) {
      getVBCoverage(setFormContext, vbCoverageData.hasCoverage);
    }
    if (ptoChoiceData) {
      getPTOChoice(setFormContext);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPath]);

  // Run after initialization so a cached autosave value is not overwritten.
  React.useEffect(() => {
    const topLevelPlanId = rawAutosaveData?.result?.leaveLogicPlanId;
    const savedLeaveLogicPlanId =
      topLevelPlanId && topLevelPlanId !== 'No Id present'
        ? topLevelPlanId
        : rawAutosaveData?.result?.data?.leaveLogicPlanId;
    if (savedLeaveLogicPlanId) {
      setFormContext((fc: IFormContext) => ({
        ...fc,
        leaveLogicPlanId: savedLeaveLogicPlanId,
      }));
    }
  }, [
    rawAutosaveData?.result?.leaveLogicPlanId,
    rawAutosaveData?.result?.data?.leaveLogicPlanId,
    setFormContext,
  ]);

  const handleOnStepSubmit = React.useCallback(
    async (event: FormSubmitClickEvent) => {
      onStepSubmit({
        event,
        steps,
        currentStepIndex,
        setSteps,
        setCurrentStepIndex,
        setFormState,
        setSubmitted,
        form,
        setShowError,
        formContext,
        setSubmitting,
        setFormContext,
        setFormStepIndex,
        setValidationErrorCount,
        autosaveParams: {
          mutateAutosavePost,
          autosaveData: {
            formName: lastPath,
            formStepIndex: currentStepIndex,
            formStepName: steps[currentStepIndex].label ?? '',
            submitted: false,
            leaveLogicPlanId: formContext.leaveLogicPlanId ?? 'No Id present',
            data: {
              ...(autosaveData?.result?.data ?? event.values),
              ...(formContext.leaveLogicPlanId
                ? { leaveLogicPlanId: formContext.leaveLogicPlanId }
                : {}),
            },
            savedTimestamp: new Date().toISOString(),
          },
        },
        refreshClaimsInProcess,
      });
      await handleSmsNotification(
        event.values,
        formContext,
        setFormContext,
        communicationPreferences,
        communicationPreferencesData,
      );
      const formName = formContext.formName;
      if (
        (formName === 'Pregnancy' ||
          formName === 'Parental Leave' ||
          formName === 'Adoption') &&
        formContext.corpLeaveIncrements === undefined
      ) {
        const corpLeaveIncrements = await getCorpLeaveIncrements(formName);
        setFormContext((fc: IFormContext) => ({
          ...fc,
          corpLeaveIncrements: corpLeaveIncrements,
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentStepIndex,
      form,
      formContext,
      steps,
      task,
      personData,
      lastPath,
      hasLlPrefillData,
      llPrefillData,
    ],
  );

  const currentStepHasLeavePlanGraph =
    currentStepIndex === formContext?.stepIndexWithLeavePlanGraph;

  const shouldShowSteps = React.useCallback(() => {
    return showSteps(form, steps, currentStepIndex);
  }, [currentStepIndex, form, steps]);

  const moveToStepCall = (
    stepperChangeEvent?: StepperChangeEvent,
    scrollToName?: string,
    stepCodename?: string,
  ) => {
    moveToStep({
      stepperChangeEvent,
      currentStepIndex,
      setCurrentStepIndex,
      setDisplayConfirmationModal,
      formContext,
      currentStepHasLeavePlanGraph:
        currentStepIndex >= (formContext.stepIndexWithLeavePlanGraph ?? -1),
      setStepGoBack,
      allowNavBack,
      setFormStepIndex,
      steps,
      formSteps: form?.elements.steps?.linkedItems,
      stepCodename,
    });
    setScrollToElement(scrollToName ?? '');
  };

  // Handle SMS preferences update on submission
  const handleSubmissionSideEffects = () => {
    if (formContext.updateSmsNotifications === true) {
      setFormContext((fc: IFormContext) => ({
        ...fc,
        updateSmsNotifications: false,
      }));
      postPreferences(formContext.communicationPreferences).catch((e) => {
        // if the update fails, we don't want to block the user from submitting the form, so we'll just log the error and move on
        console.error('Failed to update communication preferences', e);
      });
    }
  };

  const sectionName = formSteps[formStepIndex]?.elements.label?.value ?? '';

  return {
    // Form model state
    form,
    formSteps,
    formStepIndex,
    formState,
    // Step navigation
    currentStepIndex,
    setCurrentStepIndex,
    steps,
    currentStepHasLeavePlanGraph,
    // Form context
    formContext,
    setFormContext,
    // UI state
    submitted,
    submitting,
    showError,
    setShowError,
    notFound,
    autosaveIsFetching,
    prefillLoading,
    prefillApplied,
    labelMarkdown,
    validationErrorCount,
    setValidationErrorCount,
    displayConfirmationModal,
    setDisplayConfirmationModal,
    displayRestartWarningModal,
    setDisplayRestartWarningModal,
    stepGoBack,
    setStepGoBack,
    scrollToElement,
    setScrollToElement,
    sectionName,
    // Handlers
    handleOnStepSubmit,
    shouldShowSteps,
    moveToStepCall,
    handleSubmissionSideEffects,
    // Buttons needs these
    setFormStepIndex,
  };
};

export default useFormWizard;
